import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import {
  cleanForFilter,
  sortCaseInsensitive,
} from '../../../server/services/utils.service';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import { type OrgTaskWithUpdates, TaskService } from '../task.service';

@Component({
  selector: 'app-assigned-to-dialog',
  templateUrl: './assigned-to-dialog.component.html',
  styleUrls: ['./assigned-to-dialog.component.scss'],
  standalone: false,
})
export class AssignedToDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<AssignedToDialogComponent>);
  private readonly data: {
    tasks: DeepReadonly<OrgTaskWithUpdates>[];
  } = inject(MAT_DIALOG_DATA);
  private taskService = inject(TaskService);
  private authUserService = inject(AuthUserService);

  orgId = '';
  spaceId = '';
  selectUserControl = new FormControl('', { nonNullable: true });
  options: {
    title: string;
    _id: string;
    selectedForSome: boolean;
    selectedForAll: boolean;
  }[] = [];
  optionsFiltered: {
    title: string;
    _id: string;
    selectedForSome: boolean;
    selectedForAll: boolean;
  }[];
  subscriptions = new Subscription();
  tasks: DeepReadonly<OrgTaskWithUpdates>[] = [];

  constructor() {
    this.tasks = this.data.tasks;

    const orgIds = _.uniq(this.tasks.map((t) => t.orgId));
    const spaceIds = _.uniq(this.tasks.map((t) => t.spaceId));

    if (orgIds.length > 1) throw new Error('Cannot tag more than 1 orgId');
    if (spaceIds.length > 1) throw new Error('Cannot tag more than 1 spaceId');

    this.orgId = orgIds[0];
    this.spaceId = spaceIds[0];
  }

  ngOnInit(): void {
    const { orgId, spaceId } = this;
    this.options = this.authUserService
      .getSpaceUserData({ orgId, spaceId })
      .map((u) => ({
        _id: u._id,
        title: u.name,
        selectedForSome: false,
        selectedForAll: false,
      }));

    this.options.forEach((option) => {
      const matchCount = this.tasks.filter(
        (t) => t.assignedTo === option._id
      ).length;

      if (matchCount === this.tasks.length) {
        option.selectedForAll = true;
      } else if (matchCount > 0) {
        option.selectedForSome = true;
      }
    });

    const sub = this.selectUserControl.valueChanges.subscribe((value) =>
      this._filter(value)
    );
    this._filter('');
    this.subscriptions.add(sub);
  }

  private _filter(value: string) {
    const optionsFiltered = this.options;
    const filterValue = cleanForFilter(value);
    this.optionsFiltered = optionsFiltered
      .filter((option) => cleanForFilter(option.title).includes(filterValue))
      .sort((a, b) => {
        if (filterValue.length === 0) {
          // Put selected at top
          if (a.selectedForAll && !b.selectedForAll) return -1;
          if (!a.selectedForAll && b.selectedForAll) return 1;
          // Put selected for some second
          if (a.selectedForSome && !b.selectedForSome) return -1;
          if (!a.selectedForSome && b.selectedForSome) return 1;
        }
        const aTitle = cleanForFilter(a.title);
        const bTitle = cleanForFilter(b.title);
        if (aTitle.startsWith(filterValue) && !bTitle.startsWith(filterValue))
          return -1;
        if (!aTitle.startsWith(filterValue) && bTitle.startsWith(filterValue))
          return 1;
        // Sort by title
        return sortCaseInsensitive(a.title, b.title);
      });
  }

  selectionMade(event: MatAutocompleteSelectedEvent) {
    const value: string = event.option.value;
    const option = this.options.find((o) => o._id === value);
    if (!option) return;
    const assignedTo = option.selectedForAll ? null : value;
    this.tasks.forEach(({ orgId, spaceId, _id: taskId }) => {
      this.taskService.updateTask({ orgId, spaceId, taskId }, { assignedTo });
    });
    this.dialogRef?.close();
  }

  closeDialog() {
    setTimeout(() => this.dialogRef?.close(), 250);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
